import type { CityConfig } from "@/pages/city-template/types";

export const whiteRiverJunctionConfig: CityConfig = {
  city: "White River Junction",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "white-river-junction",
  route: "/cities/white-river-junction",
  title: "Expedited Passport Services in White River Junction, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in White River Junction, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving White River Junction, Hartford, Lebanon NH, Hanover NH, and all of the Upper Valley.",
  h1: "Expedited Passport Services in White River Junction, VT",
  heroSubtitle:
    "White River Junction travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the New York Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Vibrant%20view%20of%20White%20River%20Junction%20Vermont%20featuring%20the%20historic%20railroad%20junction%20with%20bridges%20over%20the%20White%20River%20and%20Connecticut%20River%2C%20charming%20downtown%20with%20colorful%20brick%20buildings%20and%20historic%20architecture%2C%20dramatic%20scenic%20river%20valley%20with%20lush%20green%20hills%2C%20warm%20golden%20hour%20light%2C%20classic%20New%20England%20river%20town%20atmosphere%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-wrj-hero-001&orientation=landscape",
  heroImageAlt: "White River Junction Vermont with historic railroad junction and riverside downtown",
  geoPosition: "43.6489; -72.3193",
  geoPlacename: "White River Junction, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "New York Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~230 miles from White River Junction via I-91",
  },
  nearbyCitiesZips: [
    { name: "White River Junction", zips: "05001" },
    { name: "Hartford", zips: "05047, 05001" },
    { name: "Lebanon", zips: "03766, 03784" },
    { name: "Hanover", zips: "03755" },
    { name: "Norwich", zips: "05055" },
    { name: "Quechee", zips: "05091" },
    { name: "Hartland", zips: "05048" },
    { name: "Woodstock", zips: "05091" },
    { name: "Royalton", zips: "05068" },
    { name: "Sharon", zips: "05065" },
    { name: "Strafford", zips: "05072" },
    { name: "Thetford", zips: "05074" },
    { name: "Vershire", zips: "05079" },
    { name: "Lyme", zips: "03768" },
    { name: "Plainfield", zips: "03781" },
    { name: "West Lebanon", zips: "03784" },
    { name: "Enfield", zips: "03748" },
    { name: "Canaan", zips: "03741" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for White River Junction travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Upper Valley & NH Border",
      desc: "White River Junction, Hartford, Lebanon NH, Hanover NH, Norwich, and all surrounding Upper Valley communities.",
    },
  ],
  testimonials: [
    {
      name: "Andrew S.",
      location: "White River Junction, VT",
      rating: 5,
      text: "I needed my passport renewed for a business trip to London in 5 days. Fast Passport Center had it back to me in White River Junction in 3 days. Their Upper Valley service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Elizabeth K.",
      location: "Lebanon, NH",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Lebanon and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "David R.",
      location: "Hanover, NH",
      rating: 5,
      text: "My son needed a passport for a study abroad program. Fast Passport Center handled the child passport process and had it ready in 4 days. Hanover families can trust them.",
      service: "Child Passport",
    },
    {
      name: "Margaret C.",
      location: "Norwich, VT",
      rating: 5,
      text: "Lost my passport before a trip to Europe. Fast Passport Center replaced it in 3 days and saved my trip. Norwich residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "wrj-1",
      name: "White River Junction Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "15 Elm Street",
      city: "White River Junction",
      state: "VT",
      zip: "05001",
      phone: "802-295-2911",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Downtown White River Junction",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "wrj-2",
      name: "Hartford Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "2001 Hartford Avenue",
      city: "Hartford",
      state: "VT",
      zip: "05047",
      phone: "802-296-2561",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Hartford",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "wrj-3",
      name: "Lebanon NH Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "11 East Park Street",
      city: "Lebanon",
      state: "NH",
      zip: "03766",
      phone: "603-448-3205",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Lebanon NH",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wrj-4",
      name: "Norwich Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "195 Main Street",
      city: "Norwich",
      state: "VT",
      zip: "05055",
      phone: "802-649-1121",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Norwich",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "wrj-5",
      name: "Hanover NH Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "33 Allen Street",
      city: "Hanover",
      state: "NH",
      zip: "03755",
      phone: "603-643-2412",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Hanover NH",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "wrj-6",
      name: "Hartford Town Clerk",
      type: "Town Clerk / Passport Services",
      address: "171 Bridge Street",
      city: "White River Junction",
      state: "VT",
      zip: "05001",
      phone: "802-295-2785",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Hartford Town Offices",
      website: "https://www.hartford-vt.org/",
      websiteLabel: "Town of Hartford",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in White River Junction, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the New York Passport Agency if I live in White River Junction?",
      a: "No — if you use Fast Passport Center, you don't need to visit the New York Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in White River Junction?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout White River Junction, Lebanon NH, and Hanover NH. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from White River Junction?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in White River Junction?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in White River Junction?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in White River Junction. We serve White River Junction and all of the Upper Valley through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to White River Junction for international travel?",
      a: "Lebanon Municipal Airport (LEB) in New Hampshire is the closest at ~5 miles. Burlington International (BTV) is ~50 miles, Albany International (ALB) is ~85 miles, and Boston Logan (BOS) is ~125 miles. Most White River Junction residents use Lebanon or Burlington for international travel.",
    },
    {
      q: "How much does expedited passport service cost in White River Junction?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in White River Junction?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in White River Junction?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in White River Junction?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does White River Junction have a local passport agency?",
      a: "No, White River Junction does not have a regional passport agency. The nearest regional agency is the New York Passport Agency in Manhattan, approximately 230 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — White River Junction", address: "various CVS locations in White River Junction", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Lebanon NH", address: "various Walgreens locations in Lebanon NH", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — White River Junction", address: "various UPS Store locations in White River Junction", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Hanover NH", address: "various FedEx Office locations in Hanover NH", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Claremont NH", address: "various Walmart locations in Claremont NH", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — White River Junction",
      address: "100 River Road, White River Junction, VT 05001",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications. Closest DMV to White River Junction.",
      featured: true,
    },
    {
      name: "NH DMV — Lebanon",
      address: "23 Dorchester Road, Lebanon, NH 03766",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "New Hampshire DMV alternative for residents across the border.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LEB",
      name: "Lebanon Municipal Airport",
      distance: "~5 miles from White River Junction",
      address: "5 Airpark Rd, West Lebanon, NH 03784",
      note: "Closest airport to White River Junction with Cape Air service to Boston. Limited commercial flights.",
      flightUrl: "https://www.lebanonairport.com/",
      flightLabel: "Lebanon Municipal",
      featured: true,
    },
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~50 miles from White River Junction",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport with more flight options. Common for White River Junction residents.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: false,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~85 miles from White River Junction",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Alternative for White River Junction travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For White River Junction travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for White River Junction residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for White River Junction residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for White River Junction residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in White River Junction, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for White River Junction residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for White River Junction travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for White River Junction residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for White River Junction residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for White River Junction residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in White River Junction." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in White River Junction." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many White River Junction travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many White River Junction travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our White River Junction team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your White River Junction address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington-vt" },
    { name: "Montpelier", route: "/cities/montpelier" },
    { name: "Rutland", route: "/cities/rutland" },
    { name: "Stowe", route: "/cities/stowe" },
    { name: "Brattleboro", route: "/cities/brattleboro" },
    { name: "Bennington", route: "/cities/bennington" },
    { name: "Middlebury", route: "/cities/middlebury" },
    { name: "Woodstock", route: "/cities/woodstock-vt" },
    { name: "Springfield", route: "/cities/springfield-vt" },
    { name: "White River Junction", route: "/cities/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves White River Junction and all of the Upper Valley as a remote courier service. We do not have a physical office in White River Junction, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeDistance: "Remote courier service — White River Junction, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all White River Junction neighborhoods and surrounding Upper Valley communities. From the historic junction to the downtown, from the Hartford town line to the New Hampshire border — we serve every White River Junction community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve White River Junction and all surrounding Upper Valley communities, including our neighbors across the river in New Hampshire. Whether you're in downtown or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our White River Junction customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes White River Junction travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "White River Junction and Upper Valley travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in White River Junction, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
};

export default whiteRiverJunctionConfig;